import CustomTooltip from '../../../shared/CustomTooltip';
import { Clear, Settings } from '@mui/icons-material';
import {
    Alert,
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Divider,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useChannelStateContext } from 'stream-chat-react';
import {
    useGetIndividualAssetQuery,
    useGetIndividualJobQuery,
    useGetIndividualReservationQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import ExperienceDetails from './ExperienceDetails';
import MembersModal from './MembersModal';
import ReservationDetails from './ReservationDetails';
import SendPrivateMessage from './SendPrivateMessage';
import JobDetails from './JobDetails';
import { ContractorRole } from '../../../enums/users';

interface ChannelDetailsProps {
    handleClose: () => void;
}

const ChannelDetails = ({ handleClose }: ChannelDetailsProps) => {
    const loggedInUser = UserRole();

    const { channel, members } = useChannelStateContext();
    const [_, setMuted] = useState<boolean>(false);

    const role = members && members[loggedInUser?.sub]?.channel_role;

    const { data: job, isLoading: jobLoading } = useGetIndividualJobQuery(
        channel?.data?.jobId as number,
        {
            skip: !channel?.data?.jobId,
        },
    );

    const experienceId = channel?.data?.experienceId ?? job?.module.id;

    const { data: experience, isLoading: experienceLoading } =
        useGetIndividualAssetQuery(`/modules/${experienceId}`, {
            skip: !experienceId,
        });

    const { data: booking } = useGetIndividualReservationQuery(
        `bookings/${channel?.data?.bookingId}`,
        { skip: !channel?.data?.experienceId },
    );

    const [membersOpen, setMembersOpen] = useState<boolean>(false);

    useEffect(() => {
        setMuted(channel.muteStatus().muted);
    }, [channel]);

    const isPublicChannel = booking?.channelId === channel?.data?.id;

    return (
        <Paper
            sx={{
                width: { xs: '100%', md: '400px' },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'hidden',
                borderRadius: 4,
                flexShrink: 0,
            }}
        >
            <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                px={2}
                pt={2}
            >
                <Typography variant='h3'>Details</Typography>
                <IconButton onClick={handleClose}>
                    <Clear />
                </IconButton>
            </Box>
            <Stack
                p={2}
                spacing={2}
                overflow='scroll'
                flex={1}
                divider={<Divider />}
                // hide scrollbar
                sx={{
                    '-ms-overflow-style': 'none',
                    '::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
            >
                {members && (
                    <Stack gap={1} alignItems='flex-start'>
                        <AvatarGroup>
                            {Object.entries(members).map(([id, member]) => (
                                <CustomTooltip
                                    title={member.user?.name}
                                    key={id}
                                >
                                    <Avatar src={member.user?.image}>
                                        {member.user?.name?.charAt(0)}
                                    </Avatar>
                                </CustomTooltip>
                            ))}
                        </AvatarGroup>
                        {loggedInUser?.role !== ContractorRole.Admin &&
                            role === 'channel_member' && (
                                <Button
                                    onClick={() => setMembersOpen(true)}
                                    startIcon={<Settings />}
                                >
                                    Members
                                </Button>
                            )}
                        <MembersModal
                            open={membersOpen}
                            onClose={() => setMembersOpen(false)}
                            channel={channel}
                            members={members}
                        />
                        {isPublicChannel &&
                            !loggedInUser?.role.includes('owner') &&
                            booking && <SendPrivateMessage booking={booking} />}
                    </Stack>
                )}
                {experience && (
                    <ExperienceDetails
                        experience={experience}
                        booking={booking}
                    />
                )}
                {job && <JobDetails job={job} />}
                {!job && !jobLoading && channel?.data?.jobId && (
                    <Alert severity='error'>
                        This job is no longer assigned to you.
                    </Alert>
                )}
                {!experienceLoading && !experience && !channel?.data?.jobId && (
                    <Alert severity='error'>
                        This experience is no longer available to rebook.
                    </Alert>
                )}
                {booking && <ReservationDetails booking={booking} />}
            </Stack>
        </Paper>
    );
};

export default ChannelDetails;
